import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { Box, Button, useTheme, useMediaQuery, TextField, InputAdornment, LinearProgress, Typography, Select, MenuItem, Popover, FormGroup, FormControlLabel, Checkbox, Grid, IconButton } from '@mui/material';
import React, { useContext, useEffect, useCallback, useState, useRef, useLayoutEffect } from 'react';
import { makeStyles } from 'tss-react/mui';
import { AuthContext, useApi, API, IconContext } from '@plumeuk/shapeshift-identity';
import { isILibraryCourse } from '@plumeuk/shapeshift-types';
import 'x2js';
import { styled } from '@mui/material/styles';

//Hook returns url path to server resources
//With strapi local dev path is relative
function useFileBaseUrl() {
    const { config } = useContext(AuthContext);
    const baseFileUrl = process.env.NODE_ENV === "production" ? "" : (config?.baseApiUrl ?? "");
    const getUrl = (e) => {
        if (e.startsWith("http"))
            return e;
        return baseFileUrl + e;
    };
    return { baseFileUrl, getUrl };
}

const useStyles$c = makeStyles()((theme) => ({
    tagContainer: {
        pointerEvents: "none",
        display: "flex"
    },
    featuredTag: {
        backgroundColor: "#FE4A49"
    },
    textWrap: {
        textWrap: "nowrap",
        textOverflow: "ellipsis",
        overflow: "hidden",
        maxWidth: "150px",
        justifyContent: "start"
    },
    iconContainer: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center"
    },
    icon: {
        width: "16px",
        maxHeight: "20px",
        marginRight: "10px",
        "& path": {
            fill: theme.palette.text.primary
        }
    },
    tag: {
        borderRadius: "6px",
        padding: "12px",
        pointerEvents: "none",
        fontSize: "12px",
        height: "20px",
        marginRight: "10px",
        textTransform: "uppercase",
        "& svg": {
            fontSize: "14px",
            paddingRight: "5px"
        }
    }
}));
const LibraryItemTags = ({ className, item, ...props }) => {
    const { classes, cx } = useStyles$c();
    return (jsx(Box, { className: cx(classes.tagContainer, className), ...props, children: item?.tags?.map((e, i) => (jsxs(Button, { "data-test-id": `course-tags-option-${e}`, className: cx(e.title.toLocaleLowerCase() === "featured" ? classes.featuredTag : "", classes.tag), children: [e.icon && jsx(Box, { className: classes.iconContainer, children: jsx("img", { className: classes.icon, src: e.icon }) }), jsx("span", { className: classes.textWrap, children: e.title })] }, `tag-${i}`))) }));
};

const useMq = () => {
    const theme = useTheme();
    const isMobileMq = useMediaQuery(`(max-width:${theme.breakpoints.values.sm - 1}px)`);
    const isTabletMq = useMediaQuery(`(min-width:${theme.breakpoints.values.sm}px) and (max-width:${theme.breakpoints.values.md}px)`);
    const isDesktopMq = useMediaQuery(`(min-width:${theme?.breakpoints.values.md + 1}px)`);
    return {
        mobile: isMobileMq,
        tablet: isTabletMq,
        desktop: isDesktopMq
    };
};

/* eslint-disable no-console */
console.log.bind(null) ;
console.warn.bind(null) ;
console.error.bind(null) ;

const useLibrary = (search, data) => {
    const [apiResult, getData, setLibrary] = useApi(data ? null : { url: API.library, data: search }, data);
    const getLibrary = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.library, data }
            : { url: API.library, data });
    };
    useEffect(() => data && setLibrary(data), [data]);
    return { items: apiResult.data, apiResult, getLibrary, setLibrary };
};

const useLibraryTags = (options, data) => {
    const [apiResult, getData, setTags] = useApi(data ? null : { url: API.libraryTags, data: options }, data);
    const getTags = (configOverride) => {
        getData(configOverride
            ? { ...configOverride, url: API.courseTags, data: options }
            : { url: API.libraryTags, data: options });
    };
    useEffect(() => data && setTags(data), [data]);
    return { tags: apiResult.data, apiResult, getTags, setTags };
};

const useLibraryCarousels = (search, data) => {
    const [apiResult, getData, setLibraryCarousels] = useApi(data ? null : { url: API.libraryCarousels, data: search }, data);
    const getLibraryCarousels = (searchOverride, configOverride) => {
        const data = searchOverride !== undefined ? searchOverride : search;
        getData(configOverride
            ? { ...configOverride, url: API.libraryCarousels, data }
            : { url: API.libraryCarousels, data });
    };
    useEffect(() => data && setLibraryCarousels(data), [data]);
    return { carousels: apiResult.data, apiResult, getLibraryCarousels, setLibraryCarousels };
};

const useCourseProgress = (slug, data) => {
    const [apiResult, getData, setProgress] = useApi(data || !slug ? null : API.courseProgress(slug), data);
    const getProgress = (slugOverride, configOverride) => {
        const _slug = slugOverride ?? slug;
        if (!_slug)
            return;
        getData(configOverride
            ? { ...configOverride, url: API.courseProgress(_slug) }
            : API.courseProgress(_slug));
    };
    useEffect(() => {
        if (data)
            setProgress(data);
    }, [data]);
    return { progress: apiResult.data, apiResult, getProgress, setProgress };
};

const LibraryDataProvider = ({ search, children, data }) => {
    const dataHook = useLibrary(search, data);
    const { getLibrary } = dataHook;
    useEffect(() => {
        if (search !== undefined && !data)
            getLibrary(search);
    }, [search]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library items" });
};

const useStyles$b = makeStyles()((theme) => ({
    searchBox: {
        boxSizing: "border-box",
        borderRadius: "6px",
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        },
        margin: "10px 0"
    }
}));
const SearchBox = ({ className, InputProps, ...props }) => {
    const { classes, cx } = useStyles$b();
    const { searchIcon } = useContext(IconContext);
    return (jsx(TextField, { className: cx(classes.searchBox, className), ...props, id: "search-box", "data-test-id": "search-input", variant: "standard", name: "search-box", type: "text", InputProps: {
            disableUnderline: true,
            placeholder: "Search",
            startAdornment: (jsx(InputAdornment, { position: "start", children: searchIcon })),
            ...InputProps
        } }));
};

const useStyles$a = makeStyles()((theme) => ({
    outlineButton: {
        border: `${theme.palette.secondary.main} 1px solid`,
        color: theme.palette.secondary.main,
        backgroundColor: "transparent",
        "&:hover": {
            backgroundColor: theme.palette.secondary.main + "10"
        }
    }
}));
const OutlineButton = ({ children, className, ...props }) => {
    const { classes, cx } = useStyles$a();
    return (jsx(Button, { className: cx(classes.outlineButton, className), ...props, children: children }));
};

const useStyles$9 = makeStyles()((theme) => ({
    loadingContainer: {
        display: "flex",
        flexGrow: 2,
        [theme.breakpoints.down("sm")]: {
            padding: "5px 20px",
            flexDirection: "row"
        },
        [theme.breakpoints.up("sm")]: {
            padding: "25px 25px 15px 35px",
            flexDirection: "column",
            justifyContent: "center",
            borderTop: "3px solid #d1d1d150"
        }
    },
    loadingBar: {
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            flexGrow: 2,
            flexDirection: "row",
            margin: "8px 15px 15px 0"
        }
    },
    percentCompleted: {
        fontSize: "15px"
    }
}));
const CourseLoadingBar = ({ className, percentCompleted, ...props }) => {
    const { classes, cx } = useStyles$9();
    const mq = useMq();
    if (percentCompleted === 100)
        return jsx(Fragment, {});
    return (jsxs(Box, { className: cx(className, classes.loadingContainer), ...props, children: [jsx(LinearProgress, { variant: "determinate", className: classes.loadingBar, value: percentCompleted }), jsxs(Typography, { className: classes.percentCompleted, variant: "body1", children: [jsxs("span", { children: [percentCompleted, "%"] }), !mq.mobile && " complete"] })] }));
};

makeStyles()((theme, { isComplete }) => ({
    courseItem: {
        cursor: isComplete ? "pointer" : "inherit",
        marginBottom: "25px"
    },
    paper: {
        display: "flex",
        borderRadius: "10px",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        }
    },
    imageContainer: {
        overflow: "hidden",
        "& img": {
            objectFit: "cover",
            width: "100%",
            height: "100%",
            borderRadius: "10px 0 0 10px",
            [theme.breakpoints.down("sm")]: {
                borderRadius: "10px 10px 0 0"
            }
        },
        [theme.breakpoints.down("sm")]: {
            width: "100%",
            height: "300px"
        },
        [theme.breakpoints.up("sm")]: {
            width: "250px",
            height: "230px",
            minWidth: "250px",
            maxWidth: "250px"
        }
    },
    infoContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "25px 35px",
        flexGrow: 3,
        "& h4": {
            marginBottom: "15px"
        },
        [theme.breakpoints.down("sm")]: {
            padding: "20px"
        }
    },
    contentContainer: {
        display: "flex",
        flexDirection: "column",
        flexGrow: 3,
        "& > div:first-of-type": {
            display: "flex",
            flexGrow: 2,
            [theme.breakpoints.down("sm")]: {
                flexDirection: "column"
            }
        }
    },
    buttonContainer: {
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        padding: "25px",
        [theme.breakpoints.down("sm")]: {
            padding: "0 20px 20px 20px"
        },
        "& > button": {
            textWrap: "no-wrap",
            margin: "10px 0",
            [theme.breakpoints.down("sm")]: {
                width: "100%",
                margin: "5px 0"
            }
        }
    },
    certificateBtn: {
        backgroundColor: "white",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        }
    },
    courseNavBtn: {
        paddingRight: "20px",
        "& svg": {
            marginLeft: "6px"
        }
    }
}));

makeStyles()((theme) => ({
    tabs: {
        [theme.breakpoints.down("sm")]: {
            display: "inherit",
            width: "100%"
        }
    },
    tab: {
        textTransform: "uppercase",
        color: theme.palette.text.primary + " !important"
    }
}));

makeStyles()((theme) => ({
    courseTable: {},
    topContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "16px",
        [theme.breakpoints.down("sm")]: {
            display: "inherit"
        }
    },
    title: {
        fontSize: "30px",
        lineHeight: "35px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "18px",
            fontWeight: 400
        }
    },
    noCoursesBody: {
        marginTop: "30px"
    },
    noCoursesForFiltersBody: {
        marginTop: "30px"
    }
}));

const useStyles$8 = makeStyles()((theme) => ({
    libraryItemProgressionActionBar: {
        justifyContent: "space-between",
        background: theme.palette.background.paper,
        cursor: "pointer",
        display: "flex",
        padding: "30px 30px",
        height: "70px",
        borderTop: "1px solid " + theme.palette.grey[200]
    },
    courseItemlink: {
        marginTop: "10px",
        marginRight: "10px"
    },
    iconContainer: {
        justifyContent: "center",
        display: "flex",
        flexDirection: "column"
    },
    leftContainer: {
        flexGrow: 3,
        justifyContent: "center",
        display: "flex",
        flexDirection: "column"
    },
    courseLoadingBar: {
        border: "none !important",
        maxWidth: "50% !important",
        padding: "0 0 0 0 !important"
    }
}));
const LibraryItemProgressionActionBar = ({ item, active }) => {
    const { classes } = useStyles$8();
    const { icon } = useContext(IconContext);
    const { progress, apiResult: progressApiResult, getProgress } = useCourseProgress();
    useEffect(() => {
        if (item.slug && (active || active === undefined) && item.enrolled)
            getProgress(item.slug);
    }, [item.slug, active, item.enrolled]);
    return (jsxs(Box, { className: classes.libraryItemProgressionActionBar, children: [jsx(Box, { className: classes.leftContainer, children: (item.enrolled && isILibraryCourse(item) && progress !== 100)
                    ? ((progress === undefined || progressApiResult.isLoading) ? jsx(LinearProgress, {}) : jsx(CourseLoadingBar, { className: classes.courseLoadingBar, percentCompleted: progress }))
                    : jsx(Typography, { className: classes.courseItemlink, children: (progress === 100) ? "Completed" : "Preview" }) }), jsx(Box, { className: classes.iconContainer, children: icon("arrowForwardIcon", { fontSize: "small" }) })] }));
};

const useStyles$7 = makeStyles()((theme) => ({
    courseItem: {
        cursor: "pointer",
        borderRadius: "10px",
        overflow: "hidden",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        width: "300px",
        transition: "transform 0.3s ease-out",
        "&:hover": {
            transform: "translate(0, -5px)"
        }
    },
    content: {
        background: theme.palette.background.paper,
        padding: "15px",
        flexGrow: 2
    },
    tagContainer: {
        padding: "15px",
        position: "absolute",
        left: 0,
        right: 0
    },
    imageContainer: {
        height: "180px",
        width: "100%",
        overflow: "hidden",
        "& img": {
            objectFit: "cover",
            width: "100%",
            height: "100%"
        }
    },
    title: {
        fontWeight: 500
    },
    description: {
        color: theme.palette.grey[400],
        marginTop: "15px"
    },
    link: {
        marginRight: "10px"
    },
    actionBar: {
        background: theme.palette.background.paper,
        cursor: "pointer",
        display: "flex",
        padding: "15px",
        borderTop: "1px solid " + theme.palette.grey[200],
        "& svg": {
            marginTop: "3px"
        }
    }
}));
const LibraryItem = ({ className, item, onClick, active, onGoToItemAction, enableProgressionBar, componentOverrides, ...props }) => {
    const { classes, cx } = useStyles$7();
    const { getUrl } = useFileBaseUrl();
    const { icon } = useContext(IconContext);
    const handleOnClick = useCallback((e) => {
        onClick?.(e);
        onGoToItemAction(item);
    }, [onGoToItemAction, onClick, item]);
    let typeClass = "";
    if (item.__type === "plugin::strapi-plugin-shapeshift-cms.course")
        typeClass = "library-item-course";
    if (item.__type === "plugin::strapi-plugin-shapeshift-cms.group")
        typeClass = "library-item-group";
    const getSmallImage = (e) => {
        return e?.formats?.small?.url ?? e.url;
    };
    return (jsxs(Box, { "data-test-id": `course-library-item-${item.slug}`, className: cx(classes.courseItem, className, typeClass), onClick: handleOnClick, ...props, children: [jsx(LibraryItemTags, { className: classes.tagContainer, item: item }), componentOverrides?.imageContainer ?? jsx(Box, { className: classes.imageContainer, children: item.featuredImage && jsx("img", { src: getUrl(getSmallImage(item.featuredImage)) }) }), componentOverrides?.content ?? jsxs(Box, { className: classes.content, children: [jsx(Typography, { className: classes.title, children: item.title }), jsx(Typography, { className: classes.description, children: (item?.description?.length ?? 0) > 120 ?
                            `${item.description?.substring(0, 100)?.trim() ?? ""}...`
                            : item.description })] }), componentOverrides?.actionBar ?? ((enableProgressionBar) ? jsx(LibraryItemProgressionActionBar, { active: active, item: item })
                : jsxs(Box, { className: classes.actionBar, children: [jsx(Typography, { className: classes.link, children: "Preview" }), icon("arrowForwardIcon", { fontSize: "small" })] })), componentOverrides?.footer ?? jsx(Fragment, {})] }));
};

const useInstructors = (data) => {
    const [apiResult, getData, setInstructors] = useApi(data ? null : API.courseInstructors, data);
    const getInstructors = (configOverride) => {
        getData(configOverride
            ? { ...configOverride, url: API.courseInstructors }
            : API.courseInstructors);
    };
    useEffect(() => data && setInstructors(data), [data]);
    return { instructors: apiResult.data, apiResult, getInstructors, setInstructors };
};

const InstructorsProvider = ({ children, data }) => {
    const dataHook = useInstructors(data);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle instructors" });
};

const useStyles$6 = makeStyles()((theme, { hasError, valid }) => ({
    select: {
        padding: "3px 15px",
        height: "43px",
        border: valid ? "1px solid #fff" : "1px solid red",
        boxSizing: "border-box",
        marginBottom: "10px",
        [theme.breakpoints.down("sm")]: {
            width: "100%"
        },
        background: "#fff"
    },
    label: {},
    errorMessage: {
        maxHeight: hasError ? 50 : 0,
        color: "#f30000",
        fontSize: "16px",
        boxSizing: "border-box",
        transition: "max-height 0.15s ease-out",
        "& p": {
            paddingTop: "5px"
        }
    }
}));
const InputSelect = ({ className, label, children, value, containerProps, error, onValidate, ...props }) => {
    const valid = error === undefined;
    const { classes, cx } = useStyles$6({ hasError: !!error, valid });
    useEffect(() => {
        if (error !== undefined) {
            onValidate?.();
        }
    }, [value]);
    return (jsxs(Box, { ...containerProps, children: [label && jsx(Typography, { className: classes.label, children: label }), jsx(Select, { variant: "standard", value: value || "", role: "combobox", ...props, className: cx(classes.select, className), disableUnderline: true, displayEmpty: true, inputProps: { "aria-label": "Without label" }, children: children }), jsx("div", { className: classes.errorMessage, children: jsx("p", { children: error ?? " " }) })] }));
};

const useStyles$5 = makeStyles()((_theme) => ({
    instructorSelect: {}
}));
const LibraryInstructorSelect = ({ className, instructorId, onChange, ...props }) => {
    const { classes, cx } = useStyles$5();
    return (jsx(InstructorsProvider, { children: ({ instructors }) => (jsxs(InputSelect, { "data-test-id": `course-library-instructor-select-${instructorId}`, className: cx(classes.instructorSelect, className), onChange: e => onChange(e.target.value), value: instructorId ?? -1, ...props, children: [jsx(MenuItem, { value: -1, children: "All Instructors" }, "instructor_all"), instructors?.map((e, i) => (jsx(MenuItem, { value: e.id, "data-test-id": `course-library-instructor-option-${e.id}`, children: e.name }, `instructor_${i}`)))] })) }));
};

const LibraryTagsProvider = ({ children, data, options }) => {
    const dataHook = useLibraryTags(options, data);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library tags" });
};

const useStyles$4 = makeStyles()((theme, { activeTags }) => ({
    tagsBtn: {
        width: "120px",
        margin: "0 20px 0 0",
        border: "1px solid #D8D8D8",
        justifyContent: "space-between",
        background: "white",
        padding: "0 15px",
        fontWeight: 400,
        color: "black",
        borderRadius: "6px",
        "& svg": {
            color: activeTags ? theme.palette.secondary.main : "grey"
        },
        "&:hover": {
            backgroundColor: "white"
        }
    },
    topContainer: {
        display: "flex",
        justifyContent: "space-between",
        marginBottom: "25px",
        width: "100%"
    },
    clearBtn: {
        color: theme.palette.primary.main,
        cursor: "pointer"
    },
    tagContainer: {},
    tagsFormContainer: {
        padding: "25px",
        width: "200px"
    }
}));
const LibraryTagSelect = ({ className, tagsSelected, label, onClear, onChange, ...props }) => {
    const { classes, cx } = useStyles$4({ activeTags: tagsSelected?.length > 0 });
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);
    const id = open ? "simple-popover" : undefined;
    const { localOfferOutlinedIcon } = useContext(IconContext);
    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };
    const handleChange = (tagId, event) => {
        onChange(tagId, event.target.checked);
    };
    return (jsx(LibraryTagsProvider, { children: ({ tags }) => jsxs(Fragment, { children: [jsxs(OutlineButton, { "data-test-id": "course-tag-select-tags-btn", className: cx(classes.tagsBtn, className), "aria-describedby": id, variant: "contained", onClick: handleClick, ...props, children: [label ?? "Tags", " ", tagsSelected?.length > 0 && jsxs(Fragment, { children: ["(x", tagsSelected.length, ")"] }), localOfferOutlinedIcon] }), jsx(Popover, { id: id, open: open, anchorEl: anchorEl, onClose: handleClose, anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left"
                    }, children: jsxs(Box, { className: classes.tagsFormContainer, children: [jsxs(Box, { className: classes.topContainer, children: [jsx(Typography, { variant: "h3", children: label ?? "Tags" }), jsx(Typography, { "data-test-id": "course-tag-select-clear-btn", className: classes.clearBtn, onClick: () => onClear(), children: "Clear all" })] }), jsx(Box, { className: classes.tagContainer, children: jsx(FormGroup, { children: tags?.map((tag, i) => (jsx(FormControlLabel, { "data-test-id": `course-library-tag-option-${tag}`, control: jsx(Checkbox, { checked: tagsSelected?.includes(tag.id), onChange: e => handleChange(tag.id, e) }), label: tag.title }, `tag_checkbox_${i}`))) }) })] }) })] }) }));
};

const useStyles$3 = makeStyles()((theme) => ({
    librarySearch: {
        margin: "10px",
        gap: "10px",
        display: "flex",
        [theme.breakpoints.down("sm")]: {
            flexDirection: "column"
        },
        "& > *": {
            height: "43px"
        }
    },
    searchBox: {
        margin: "0",
        flexGrow: "2",
        border: "1px solid #D8D8D8"
    },
    select: {
        margin: "0",
        border: "1px solid #D8D8D8",
        borderRadius: "6px"
    }
}));
const LibrarySearch = ({ componentOverrides, className, value, onChange, textFieldProps, ...props }) => {
    const { classes, cx } = useStyles$3();
    const onTagSelectChange = (tag, status) => {
        onChange(prev => {
            if (!prev.tags) {
                return status ? { ...prev, tags: [tag] } : prev;
            }
            //remove tag
            if (prev.tags.includes(tag) && !status) {
                const newTags = prev.tags.filter(e => e !== tag);
                return { ...prev, tags: [...newTags] };
            }
            //add tag
            if (!prev.tags.includes(tag) && status) {
                return { ...prev, tags: [...prev.tags, tag] };
            }
            //no action
            return prev;
        });
    };
    const onTagSelectClear = () => onChange(prev => ({ ...prev, tags: [] }));
    return (jsxs(Box, { className: cx(classes.librarySearch, className), ...props, children: [componentOverrides?.header ?? jsx(Fragment, {}), jsx(SearchBox, { className: classes.searchBox, value: value.searchTerm, onChange: e => onChange({ ...value, searchTerm: e.target.value ?? "" }), ...textFieldProps }), jsx(LibraryInstructorSelect, { className: cx(classes.select, "instructor-select"), instructorId: value?.instructorId, onChange: e => onChange({ ...value, instructorId: e === -1 ? undefined : e }) }), componentOverrides?.libraryTagSelect?.({
                className: cx(classes.select, "library-tag-select"),
                tagsSelected: value.tags ?? [],
                onClear: onTagSelectClear,
                onChange: onTagSelectChange
            }) ??
                (jsx(LibraryTagSelect, { className: cx(classes.select, "library-tag-select"), tagsSelected: value.tags ?? [], onClear: onTagSelectClear, onChange: onTagSelectChange })), componentOverrides?.footer ?? jsx(Fragment, {})] }));
};

const useStyles$2 = makeStyles()((theme) => ({
    library: {},
    title: {
        margin: "10px",
        lineHeight: "35px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "35px"
        }
    },
    search: {
        marginTop: "35px"
    },
    itemContainer: {
        padding: "30px 10px"
    },
    libraryItem: {
        width: "100%",
        height: "100%"
    },
    noneFoundContainer: {
        padding: "30px"
    }
}));
const Library = ({ componentOverrides, className, data, onGoToItemAction, children, ...props }) => {
    const { classes, cx } = useStyles$2();
    const [searchValue, setSearchValue] = useState({ searchTerm: "" });
    return (jsxs(Box, { className: cx(classes.library, className), ...props, children: [componentOverrides?.header ?? jsx(Fragment, {}), jsx(Typography, { variant: "h2", className: classes.title, children: "Library" }), componentOverrides?.librarySearch ?? jsx(LibrarySearch, { className: classes.search, value: searchValue, onChange: setSearchValue }), jsx(Grid, { container: true, spacing: 2.5, className: classes.itemContainer, children: jsx(LibraryDataProvider, { data: data, search: searchValue, children: ({ items, apiResult }) => (jsxs(Fragment, { children: [children?.(items, apiResult) ?? items?.map((item, i) => (jsx(Grid, { item: true, xs: 12, sm: 6, md: 4, lg: 3, "data-test-id": `course-library-item-${item.slug}`, children: jsx(LibraryItem, { item: item, className: classes.libraryItem, onGoToItemAction: () => onGoToItemAction?.(item.slug) }) }, "Library-item-" + i))), (!children && items?.length === 0) && jsx(Box, { className: classes.noneFoundContainer, children: "No results found matching filters" })] })) }) }), componentOverrides?.footer ?? jsx(Fragment, {})] }));
};

const LibraryCarouselsDataProvider = ({ search, children, data }) => {
    const dataHook = useLibraryCarousels(search, data);
    const { getLibraryCarousels } = dataHook;
    useEffect(() => {
        if (search !== undefined && !data)
            getLibraryCarousels(search);
    }, [search]);
    return children?.(dataHook)
        ?? jsx("div", { children: "Please implement children to handle library carousels" });
};

const useStyles$1 = makeStyles()((theme) => ({
    carousel: {},
    title: {
        fontWeight: 800,
        margin: "10px",
        fontSize: "34px",
        lineHeight: "35px",
        [theme.breakpoints.down("sm")]: {
            fontSize: "35px"
        }
    },
    itemsContainer: {
        display: "flex",
        gap: "2%",
        padding: "30px 10px",
        overflow: "hidden"
    },
    itemContainer: {
        minWidth: "23.5%",
        maxWidth: "23.5%",
        [theme.breakpoints.down("md")]: {
            minWidth: "100%",
            maxWidth: "100%"
        }
    },
    libraryItem: {
        width: "100%",
        height: "100%",
        maxWidth: "400px",
        borderRadius: "18px",
        border: "1px solid #d1d1d1"
    },
    pageIndicator: {
        width: "10px",
        height: "10px",
        borderRadius: "50%",
        background: "grey",
        marginRight: "10px"
    },
    activePageIndicator: {
        background: theme.palette.secondary.main
    }
}));
const ArrowButtons = styled(IconButton)(() => ({
    color: "white",
    backgroundColor: "grey",
    "&:hover": {
        backgroundColor: "grey"
    },
    "& svg": {
        fontSize: "30px"
    },
    width: "50px",
    height: "50px"
}));
const LibraryCarousel = ({ carousel, onGoToItemAction, componentOverrides, className, ...props }) => {
    const mq = useMq();
    const carouselRef = useRef(null);
    const { classes, cx } = useStyles$1();
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = mq.desktop ? 4 : 1;
    const { arrowBackIcon, arrowForwardIcon } = useContext(IconContext);
    useLayoutEffect(() => {
        if (!carouselRef.current)
            return;
        carouselRef.current?.scrollTo?.({ left: 0, behavior: "smooth" });
        setCurrentPage(0);
    }, [carousel.items]);
    const atMaxScroll = !carousel.items || currentPage === Math.ceil(carousel.items.length / itemsPerPage) - 1;
    const onClickCarouselForward = (ref) => {
        if (atMaxScroll)
            return;
        ref.current?.scrollBy({ left: ref.current.offsetWidth, behavior: "smooth" });
        setCurrentPage(currentPage + 1);
    };
    const atMinScroll = currentPage === 0;
    const onClickCarouselBack = (ref) => {
        if (atMinScroll)
            return;
        ref.current?.scrollBy({ left: -ref.current.offsetWidth, behavior: "smooth" });
        setCurrentPage(currentPage - 1);
    };
    return (jsxs(Box, { className: cx(classes.carousel, className), ...props, children: [jsxs(Box, { display: "flex", justifyContent: "space-between", alignItems: "center", children: [jsx(Typography, { variant: "h3", className: classes.title, children: carousel.title }), !(atMaxScroll && atMinScroll) && jsxs(Box, { display: 'flex', gap: 1, paddingRight: "24px", children: [jsx(ArrowButtons, { sx: { opacity: atMinScroll ? 0.3 : 1 }, onClick: () => onClickCarouselBack(carouselRef), children: arrowBackIcon }), jsx(ArrowButtons, { sx: { opacity: atMaxScroll ? 0.3 : 1 }, onClick: () => onClickCarouselForward(carouselRef), children: arrowForwardIcon })] })] }), jsx(Box, { ref: carouselRef, className: classes.itemsContainer, children: carousel.items?.map((item, i) => {
                    const active = i < ((currentPage + 1) * itemsPerPage) && i >= currentPage * itemsPerPage;
                    return jsx(Box, { className: classes.itemContainer, children: componentOverrides?.item?.({ item, active, onGoToItemAction, className: classes.libraryItem }) ?? jsx(LibraryItem, { item: item, active: active, className: classes.libraryItem, onGoToItemAction: onGoToItemAction }) }, `library-item-${i}`);
                }) }), jsx(Box, { display: "flex", justifyContent: "center", marginTop: "20px", children: Array.from({ length: Math.ceil(carousel.items.length / itemsPerPage) }, (_, i) => i).map(index => jsx(Box, { className: cx(classes.pageIndicator, index === currentPage ? classes.activePageIndicator : "") }, index)) })] }));
};

const useStyles = makeStyles()(() => ({
    libraryCarousels: {},
    noneFoundContainer: {
        padding: "30px"
    },
    libraryCarousel: {
        marginBottom: "56px",
        minHeight: "400px"
    }
}));
const LibraryCarousels = ({ componentOverrides, searchValue, className, data, onGoToItemAction, children, ...props }) => {
    const { classes, cx } = useStyles();
    return (jsxs(Box, { className: cx(classes.libraryCarousels, className), ...props, children: [componentOverrides?.header ?? jsx(Fragment, {}), jsx(LibraryCarouselsDataProvider, { data: data, search: searchValue, children: ({ carousels, apiResult }) => (jsxs(Fragment, { children: [children?.(carousels, apiResult) ?? carousels?.map((carousel, i) => componentOverrides?.libraryCarousel?.({ carousel, onGoToItemAction, className: classes.libraryCarousel, key: `Library-carousel-${i}` }) ?? (apiResult.isLoading ? jsx(LinearProgress, {}) : jsx(LibraryCarousel, { carousel: carousel, onGoToItemAction: onGoToItemAction, className: classes.libraryCarousel }, "Library-carousel-" + i))), (!children && carousels?.length === 0) && jsx(Box, { className: classes.noneFoundContainer, children: "No results found matching filters" })] })) }), componentOverrides?.footer ?? jsx(Fragment, {})] }));
};

export { Library, LibraryCarousel, LibraryCarousels, LibraryItem, LibraryItemTags, LibrarySearch, LibraryTagSelect };
